import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Box, Flex, Spinner, Text, useColorMode } from 'theme-ui';
import theme from '../../../theme';
import TablePagination from '../../Tabels/Pagination';
import FilterPopup from '../../forms-component/filterPopup';
import { TabelClassname } from '../../../utils/Classname';

export { getTradeBadgeInfo, type TradeBadgeResult } from './deliveryModalUtils';

interface DeliveriesTableProps<T extends object> {
  data: T[];
  columns: (ColumnDef<T> & { filterType?: string; options?: unknown })[];
  loader: boolean;
  totalCount: number;
  perPageCount: number;
  currentPage: number;
  enableColumnFilters?: boolean;
  onPageChange: (page: number | string) => void;
  onPerPageChange: (value: string) => void;
}

const DeliveriesTable = <T extends object>({
  data,
  columns,
  loader,
  totalCount,
  perPageCount,
  currentPage,
  enableColumnFilters = true,
  onPageChange,
  onPerPageChange,
}: DeliveriesTableProps<T>) => {
  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';
  const thBg = isDark ? '#374358' : '#f7efe5';
  const thColor = isDark ? '#dadada' : '#0a0a0a';
  const tdBg = isDark ? '#282e42' : '#ffffff';
  const borderColor = isDark ? '#374358' : '#E5E7EB';
  const CELL_PAD = '6px 10px';

  const lastIdx = columns.length - 1;
  const secondLastIdx = columns.length - 2;

  const firstThRef = useRef<HTMLTableCellElement>(null);
  const lastThRef = useRef<HTMLTableCellElement>(null);
  const secondLastThRef = useRef<HTMLTableCellElement>(null);
  const [colWidths, setColWidths] = useState({ first: 0, last: 0, secondLast: 0 });

  useLayoutEffect(() => {
    setColWidths({
      first: firstThRef.current?.getBoundingClientRect().width || 0,
      last: lastThRef.current?.getBoundingClientRect().width || 0,
      secondLast: secondLastThRef.current?.getBoundingClientRect().width || 0,
    });
  }, [columns.length]);

  const getStickyStyle = (
    index: number,
    bg: string,
    isHeader = false,
  ): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'sticky',
      zIndex: isHeader ? 15 : 3,
      background: bg,
    };

    if (index === 0) return { ...base, left: 0 };
    if (index === 1 && colWidths.first > 0) {
      return { ...base, left: colWidths.first, boxShadow: '2px 0 4px -2px rgba(0,0,0,0.18)' };
    }
    if (index === lastIdx) {
      return { ...base, right: 0, boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.18)' };
    }
    if (index === secondLastIdx && colWidths.last > 0) {
      return { ...base, right: colWidths.last, boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.18)' };
    }
    return {};
  };

  const cellBorder: React.CSSProperties = {
    padding: CELL_PAD,
    fontSize: '14px',
    borderBottom: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnFilters,
    pageCount: Math.ceil(totalCount / perPageCount),
    manualPagination: true,
  });

  const rows = table.getRowModel().rows;

  const renderTh = (header: ReturnType<typeof table.getHeaderGroups>[0]['headers'][0], index: number) => {
    const isFirst = index === 0;
    const isLast = index === lastIdx;
    const isSecondLast = index === secondLastIdx;
    const sticky = getStickyStyle(index, thBg, true);

    return (
      <th
        key={header.id}
        ref={isFirst ? firstThRef : isLast ? lastThRef : isSecondLast ? secondLastThRef : undefined}
        className="th align-middle border-top-0 border-bottom-0"
        style={{
          ...cellBorder,
          background: thBg,
          color: thColor,
          whiteSpace: 'nowrap',
          borderLeft: index === 1 ? `1px solid ${borderColor}` : undefined,
          ...sticky,
        }}
      >
        {header.column.getCanFilter() && (
          <FilterPopup column={header.column.columnDef} id={`del-col-${index}`} />
        )}
        <Text
          variant="Poppins16Normal25lh"
          as="span"
          sx={{ color: thColor, fontSize: '14px' }}
          className="text-center position-relative"
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </Text>
      </th>
    );
  };

  return (
    <Box sx={{ paddingLeft: '15px', paddingRight: '15px', paddingBottom: '10px' }}>
      <Box
        sx={{
          position: 'relative',
          borderRadius: '10px',
          border: `1px solid ${theme?.colors?.muted}`,
          overflow: 'auto',
          height: 'calc(100vh - 360px)',
          minHeight: 'calc(100vh - 360px)',
          maxHeight: 'calc(100vh - 360px)',
        }}
      >
        <table
          className={`${TabelClassname?.PrimaryTabel} mb-0 table-hover`}
          style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto', padding: 0 }}
        >
          <thead style={{ position: 'sticky', top: 0, zIndex: 12 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => renderTh(header, index))}
              </tr>
            ))}
          </thead>

          <tbody>
            {!loader &&
              rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  className={`table-hover-row ${isDark ? 'dark-mode' : 'light-mode'}`}
                  style={{
                    borderBottom:
                      rowIndex + 1 === rows.length ? 'none' : `1px solid ${borderColor}`,
                  }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const hasOwnColor =
                      cell.column.columnDef.header === 'Stages Name List' ||
                      cell.column.columnDef.header === 'Highlighters';
                    const sticky = getStickyStyle(cellIndex, tdBg);
                    return (
                      <td
                        key={cell.id}
                        style={{
                          ...cellBorder,
                          background: tdBg,
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderLeft: cellIndex === 1 ? `1px solid ${borderColor}` : undefined,
                          ...(hasOwnColor ? {} : { color: isDark ? '#ffffff' : '#0a0a0a' }),
                          ...sticky,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>

        {/* Spinner overlay */}
        {loader && (
          <Flex
            sx={{
              position: 'absolute',
              inset: 0,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDark ? 'rgba(40,46,66,0.6)' : 'rgba(255,255,255,0.6)',
              zIndex: 20,
              borderRadius: '10px',
            }}
          >
            <Spinner size={40} />
          </Flex>
        )}

        {/* Empty state */}
        {!loader && data.length === 0 && (
          <Flex
            sx={{
              position: 'absolute',
              inset: 0,
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 5,
            }}
          >
            <Text sx={{ fontSize: '14px', fontWeight: 500, color: thColor }}>No Data Found!</Text>
          </Flex>
        )}
      </Box>

      <Box className="sticky-navigation" sx={{ '& > div': { mt: '18px !important' } }}>
        <TablePagination
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          TotalLength={totalCount}
          perPageCount={perPageCount}
          CurrentPage={currentPage}
        />
      </Box>
    </Box>
  );
};

export default DeliveriesTable;
